import React from 'react';

import SelectSystemCode from '../../../../components/Dropdown/SelectSystemCode';
import Text from '../../../../components/Typography/Text';
import ImageAttachment from '../../../../components/Misc/ImageAttachment';
import MaskedField from '../../../../components/Textfields/MaskedField';
import TextField from '../../../../components/Textfields/TextField';
import DateTimePicker from '../../../../components/Textfields/DateTimePicker';
import AutoCompleteCountry from '../../../../components/AutoComplete/AutoCompleteCountry';
import AutoCompleteState from '../../../../components/AutoComplete/AutoCompleteState';
import Checkbox from '../../../../components/Misc/Checkbox';
import { useAccountContext } from '../Data/accountContext';
import { isDisabledJumio } from '../../../../lib/data/common';
import InputMask from 'react-input-mask';

const FormIdentificationInput = ({ validation, objectName, index }) => {
  const { account, setAccount, disableUpdate } = useAccountContext();
  const SSN_PATTERN = '###-##-####';

  const setData = (data) => {
    const copy = { ...account };
    if (objectName === 'primaryOwner') {
      copy.primaryOwner.identification = { ...data };
    } else {
      copy[objectName][index].identification = { ...data };
    }
    setAccount(copy);
  };

  const handleChange = (e) => {
    const input = e.currentTarget.name ? e.currentTarget : e.target;
    const copy = { ...account };
    const value =
      input.type === 'checkbox' ? input.checked : input.formattedValue || input.value;

    if (objectName === 'primaryOwner') {
      copy.primaryOwner.identification[input.name] = value;

      if (
        (input.name === 'taxCountry' && value === 'US') ||
        (input.name === 'isSameTaxCountry' && copy.primaryOwner.country === 'US')
      ) {
        copy.primaryOwner.identification.taxNo = '';
      }
    } else {
      copy[objectName][index].identification[input.name] = value;
      if (
        (input.name === 'taxCountry' && value === 'US') ||
        (input.name === 'isSameTaxCountry' && copy[objectName][index].country === 'US')
      ) {
        copy[objectName][index].identification.taxNo = '';
      }
    }

    setAccount(copy);
  };

  let owner = account?.primaryOwner || {};
  let val = validation?.primaryOwner || {};
  if (objectName !== 'primaryOwner') {
    owner = account[objectName][index] || {};
    val = validation[objectName][index] || {};
  }
  let data = owner.identification || {};

  return (
    <React.Fragment>
      <div className="grd-row">
        <Text
          mb={10}
          mr={5}
          label="Government ID - US Driver's License or International Passport"
        />
        <span style={{ color: 'red', fontWeight: 'bold' }}>*</span>
      </div>
      <div className="grd-row nm">
        <div className="grd-cell-none mr">
          <ImageAttachment
            id={'imgGovernmentId'}
            error={!!val.imgGovernmentId}
            msg={val.imgGovernmentId}
            thumbnail={data.imgGovernmentId || ''}
            data={data}
            setNewData={setData}
            index={index}
            hidButton={isDisabledJumio ? false : objectName === 'primaryOwner'}
            disabled={disableUpdate}
          />
        </div>
        <div className="grd-cell">
          <div className="grd-row nm">
            <div className="grd-cell">
              <SelectSystemCode
                required={true}
                error={!!val.identificationType}
                msg={val.identificationType}
                name="identificationType"
                label="Identification Type"
                placeholder="Identification Type"
                type="Identification"
                subType="Client"
                value={data.identificationType || ''}
                onChange={handleChange}
                disabled={disableUpdate || (data.identificationType && !isDisabledJumio)}
              />
            </div>
            <div className="grd-cell">
              {['SSN', 'TIN', 'GIIN'].includes(data.identificationType) ? (
                <InputMask
                  mask={
                    data.identificationType === 'SSN'
                      ? '999-99-9999'
                      : data.identificationType === 'GIIN'
                        ? '******.*****.**.***'
                        : '99-9999999'
                  }
                  value={data.identificationNo || ''}
                  beforeMaskedValueChange={(newId, oldId) => {
                    if (newId.value !== oldId.value) {
                      handleChange({
                        currentTarget: {
                          name: 'identificationNo',
                          value: newId.value,
                        },
                      });
                    }

                    return {
                      value: newId.value,
                      selection: newId.selection,
                    };
                  }}
                  disabled={disableUpdate || (data.identificationNo && !isDisabledJumio)}
                >
                  {() => (
                    <TextField
                      fullWidth={true}
                      required={true}
                      error={!!val.identificationNo}
                      helperText={val.identificationNo}
                      id="identificationNo"
                      name="identificationNo"
                      label="Identification No."
                      placeholder="Identification No."
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                </InputMask>
              ) : (
                <TextField
                  required={true}
                  error={!!val.identificationNo}
                  msg={val.identificationNo}
                  max={data.identificationType === 'Passport' ? 30 : 13}
                  name="identificationNo"
                  label="Identification No."
                  placeholder="Identification No."
                  type="text"
                  value={data.identificationNo || ''}
                  onChange={handleChange}
                  disabled={disableUpdate || (data.identificationNo && !isDisabledJumio)}
                />
              )}
            </div>
          </div>
          <div className="grd-row">
            <div className="grd-cell">
              <DateTimePicker
                required={true}
                error={!!val.issueDate}
                msg={val.issueDate}
                type="date"
                name="issueDate"
                label="Issue Date"
                value={data.issueDate || ''}
                onChange={handleChange}
                disabled={disableUpdate}
              />
            </div>
            <div className="grd-cell">
              <DateTimePicker
                required={true}
                error={!!val.expirationDate}
                msg={val.expirationDate}
                type="date"
                name="expirationDate"
                label="Expiration Date"
                value={data.expirationDate || ''}
                onChange={handleChange}
                disabled={disableUpdate}
              />
            </div>
          </div>
          <div className="grd-row">
            <div className="grd-cell">
              <Checkbox
                name="isSameCountryOfIssuance"
                mt={15}
                ml={-3}
                label="My country of ID is the same as my physical address."
                checked={data.isSameCountryOfIssuance}
                onChange={handleChange}
                disabled={disableUpdate}
              />
            </div>
            {!!val.isSameCountryOfIssuance && (
              <Text
                mb={25}
                variant="body"
                error={!!val.isSameCountryOfIssuance}
                label={val.isSameCountryOfIssuance}
              />
            )}
            {!data.isSameCountryOfIssuance && (
              <div className="grd-cell">
                <AutoCompleteCountry
                  required={true}
                  error={!!val.countryOfIssuance}
                  msg={val.countryOfIssuance}
                  name="countryOfIssuance"
                  label="Country of Issuance"
                  placeholder="Country of Issuance"
                  value={data.countryOfIssuance || ''}
                  onChange={handleChange}
                  disabled={disableUpdate}
                />
              </div>
            )}
          </div>
          {(owner.country === 'US' && data.isSameTaxCountry) ||
            data.taxCountry === 'US' ? (
            <div className="grd-row">
              <div className="grd-cell">
                <AutoCompleteState
                  name="taxState"
                  label="State"
                  placeholder="State"
                  type="text"
                  value={data.taxState || ''}
                  onChange={handleChange}
                  disabled={disableUpdate}
                />
              </div>
              <div className="grd-cell"></div>
            </div>
          ) : (
            <div className="grd-row">
              <div className="grd-cell">
                <TextField
                  name="taxState"
                  label="State"
                  placeholder="State"
                  type="text"
                  value={data.taxState || ''}
                  onChange={handleChange}
                  disabled={disableUpdate}
                />
              </div>
              <div className="grd-cell"></div>
            </div>
          )}
        </div>
      </div>

      <div className="grd-row">
        <div className="grd-cell">
          <DateTimePicker
            required={true}
            error={!!val.dateOfBirth}
            msg={val.dateOfBirth}
            type="date"
            name="dateOfBirth"
            label="Birth Date"
            value={data.dateOfBirth || ''}
            onChange={handleChange}
            disabled={disableUpdate}
          />
        </div>
        {(owner.country === 'US' && data.isSameTaxCountry) || data.taxCountry === 'US' ? (
          <div className="grd-cell">
            <MaskedField
              required={
                owner.country === 'US' &&
                (data.isSameTaxCountry || data.taxCountry === 'US')
              }
              error={!!val.taxNo}
              msg={val.taxNo}
              tooltip={val.taxNoTooltip}
              name="taxNo"
              label="SSN"
              placeholder="SSN"
              format={SSN_PATTERN}
              value={data.taxNo || ''}
              onChange={handleChange}
              disabled={disableUpdate}
            />
          </div>
        ) : (
          <div className="grd-cell">
            <TextField
              max={30}
              name="taxNo"
              label="Foreign Tax ID"
              placeholder="Foreign Tax ID"
              type="text"
              value={data.taxNo || ''}
              onChange={handleChange}
              disabled={disableUpdate}
            />
          </div>
        )}
      </div>
      <div className="grd-col">
        <Checkbox
          ml={-3}
          required={true}
          name="isSameTaxCountry"
          label="My country of taxation is the same as my physical address."
          checked={data.isSameTaxCountry}
          onChange={handleChange}
          disabled={disableUpdate}
        />
        {!!val.isSameTaxCountry && (
          <Text
            mb={25}
            variant="body"
            error={!!val.isSameTaxCountry}
            label={val.isSameTaxCountry}
          />
        )}

        {!data.isSameTaxCountry && (
          <AutoCompleteCountry
            required={true}
            error={!!val.taxCountry}
            msg={val.taxCountry}
            mt={20}
            mb={10}
            name="taxCountry"
            label="Tax Country"
            placeholder="Tax Country"
            value={data.taxCountry || ''}
            onChange={handleChange}
            disabled={disableUpdate}
          />
        )}
      </div>
      <div className="grd-col">
        <Checkbox
          name="isSameCitizenship"
          ml={-3}
          label="My country of citizenship is the same as my physical address."
          checked={data.isSameCitizenship}
          onChange={handleChange}
          disabled={disableUpdate}
        />
        {!!val.isSameCitizenship && (
          <Text
            mb={25}
            variant="body"
            error={!!val.isSameCitizenship}
            label={val.isSameCitizenship}
          />
        )}

        {!data.isSameCitizenship && (
          <AutoCompleteCountry
            required={true}
            error={!!val.citizenship}
            msg={val.citizenship}
            mt={20}
            name="citizenship"
            label="Citizenship"
            placeholder="Citizenship"
            value={data.citizenship || ''}
            onChange={handleChange}
            disabled={disableUpdate}
          />
        )}
      </div>
    </React.Fragment>
  );
};

export default FormIdentificationInput;
